import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { exportTemplateToPNG } from '../lib/exportTemplate';
import { toast } from 'sonner@2.0.3';

interface ExportTemplateButtonProps {
  /**
   * Данные шаблона для экспорта
   */
  backgroundColor: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string; // Добавляем для гарантированной загрузки
  fontName?: string; // Добавляем для гарантированной загрузки
  fontSize?: number; // Размер шрифта из шаблона
  logoUrl?: string;
  logoPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-right';
  logoSize?: number;
  textLineSpacing?: number;
  imageUrl?: string;
  text?: string;
  textUppercase?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  fontBold?: boolean; // Жирный шрифт
  fitMode?: 'fill' | 'fit'; // Режим отображения медиа
  showLine?: boolean; // Показывать ли декоративную линию
  lineColor?: string; // Цвет линии
  lineHeight?: number; // Высота линии в px
  linePosition?: 'top' | 'bottom'; // Позиция линии
  textShadowEnabled?: boolean; // Обводка
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean; // Мягкая тень
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;

  // Макет поста. Без них экспорт молча падал в дефолтный 'bottom-bar' и рисовал
  // сплошную плашку backgroundColor вместо PNG-каркаса — скачанный файл не
  // совпадал ни с превью, ни с тем, что уходит в публикацию.
  postLayout?: 'bottom-bar' | 'top-card' | 'frame';
  accentColor?: string;
  cardColor?: string;
  cardInset?: number;
  bandHeight?: number;
  frameUrl?: string;
  frameTextX?: number;
  frameTextY?: number;
  frameTextWidth?: number;
  frameTextVAlign?: 'top' | 'center' | 'bottom';
  autoFontMax?: number;
  bandGrow?: boolean;

  /**
   * Ref к PostTemplate для измерения реального размера
   */
  templateRef?: React.RefObject<HTMLDivElement>;
  
  /**
   * Название файла для скачивания
   */
  fileName?: string;
  
  /**
   * Размер кнопки
   */
  size?: 'sm' | 'default' | 'lg';
  
  /**
   * Вариант кнопки
   */
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  
  /**
   * Полная ширина
   */
  fullWidth?: boolean;
}

export function ExportTemplateButton({
  backgroundColor,
  useGradient,
  backgroundColorEnd,
  backgroundAlpha,
  backgroundAlphaEnd,
  textColor,
  useTwoColors,
  textColorSecondary,
  fontFamily,
  fontUrl,
  fontName,
  fontSize,
  logoUrl,
  logoPosition,
  logoSize,
  textLineSpacing,
  imageUrl,
  text,
  textUppercase,
  textAlign,
  fontBold,
  fitMode,
  postLayout,
  accentColor,
  cardColor,
  cardInset,
  bandHeight,
  frameUrl,
  frameTextX,
  frameTextY,
  frameTextWidth,
  frameTextVAlign,
  autoFontMax,
  bandGrow,
  showLine,
  lineColor,
  lineHeight,
  linePosition,
  textShadowEnabled,
  textShadowColor,
  textShadowBlur,
  textShadowWidth,
  textSoftShadowEnabled,
  textSoftShadowColor,
  textSoftShadowBlur,
  textSoftShadowOffsetX,
  textSoftShadowOffsetY,
  textSoftShadowOpacity,
  textShadowOpacity,
  templateRef,
  fileName,
  size = 'default',
  variant = 'default',
  fullWidth = false,
}: ExportTemplateButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Измеряем реальный размер PostTemplate
      let sourceWidth = 400; // значение по умолчанию (max-w-md)
      
      if (templateRef?.current) {
        const rect = templateRef.current.getBoundingClientRect();
        sourceWidth = rect.width;
        console.log('📏 Measured template width:', sourceWidth);
      }
      
      await exportTemplateToPNG({
        fileName: fileName || `instagram-post-${Date.now()}.png`,
        backgroundColor,
        useGradient,
        backgroundColorEnd,
        backgroundAlpha,
        backgroundAlphaEnd,
        textColor,
        useTwoColors,
        textColorSecondary,
        fontFamily,
        fontUrl,
        fontName,
        fontSize,
        logoUrl,
        logoPosition,
        logoSize,
        textLineSpacing,
        imageUrl,
        text,
        textUppercase,
        textAlign,
        fontBold,
        fitMode,
        postLayout,
        accentColor,
        cardColor,
        cardInset,
        bandHeight,
        frameUrl,
        frameTextX,
        frameTextY,
        frameTextWidth,
        frameTextVAlign,
        autoFontMax,
        bandGrow,
        showLine,
        lineColor,
        lineHeight,
        linePosition,
        textShadowEnabled,
        textShadowColor,
        textShadowBlur,
        textShadowWidth,
        textSoftShadowEnabled,
        textSoftShadowColor,
        textSoftShadowBlur,
        textSoftShadowOffsetX,
        textSoftShadowOffsetY,
        textSoftShadowOpacity,
        textShadowOpacity,
        sourceWidth,
      });

      toast.success('Изображение успешно скачано!');
    } catch (error) {
      console.error('❌ Export error:', error);
      toast.error('Ошибка при экспорте изображения');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isExporting}
      size={size}
      variant={variant}
      className={fullWidth ? 'w-full' : ''}
    >
      {isExporting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Экспорт...
        </>
      ) : (
        <>
          <Download className="h-4 w-4" />
          Скачать как PNG
        </>
      )}
    </Button>
  );
}